/**
 * @file ChannelBadgeSection.tsx
 * @description Секция «Бейдж витрины» для ChannelCardDrawer (phase18.3, promo.md P4).
 *
 * Ручной бейдж карточки канала: BESTSELLER (гейт ≥10 оплаченных заказов —
 * проверяется сервером), COMING_SOON, SALE, null — снять. Опциональный срок
 * `badgeUntil` — по истечении бейдж снимается автоматически (сервер).
 * Вынесено из ChannelCardFields.tsx, чтобы уложиться в лимит ≤200 строк/файл.
 */

import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { SelectField } from "../../../../components/ui/SelectField";
import { MANUAL_BADGE_LABELS, type ManualBadgeKind } from "../../../../lib/api-channel-listings";

/** Варианты бейджа — «без бейджа» первым. */
const BADGE_OPTIONS = [
  { value: "", label: "Без бейджа" },
  ...(Object.keys(MANUAL_BADGE_LABELS) as ManualBadgeKind[]).map((k) => ({ value: k, label: MANUAL_BADGE_LABELS[k] })),
];

/** ISO → значение <input type="datetime-local"> (локальное время). */
export function toLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

/** Значение datetime-local → ISO (UTC) для отправки на сервер. */
export function toIso(local: string): string | null {
  if (!local.trim()) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function ChannelBadgeSection({
  manualBadge,
  badgeUntil,
  onBadgeChange,
  onUntilChange,
}: {
  manualBadge: "" | ManualBadgeKind;
  /** Значение <input type="datetime-local">. */
  badgeUntil: string;
  onBadgeChange: (v: "" | ManualBadgeKind) => void;
  onUntilChange: (v: string) => void;
}) {
  return (
    <section className="flex flex-col gap-2">
      <h4 className="type-caption font-medium text-on-surface-variant">Бейдж витрины</h4>
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 flex flex-col gap-1">
          <Label htmlFor="ch-badge" className="text-xs">Бейдж</Label>
          <SelectField
            id="ch-badge"
            ariaLabel="Бейдж"
            value={manualBadge}
            onChange={(v) => onBadgeChange(v as "" | ManualBadgeKind)}
            options={BADGE_OPTIONS}
          />
        </div>
        {manualBadge ? (
          <div className="flex-1 flex flex-col gap-1">
            <Label htmlFor="ch-badge-until" className="text-xs">Действует до (опц.)</Label>
            <Input
              id="ch-badge-until"
              type="datetime-local"
              value={badgeUntil}
              onChange={(e) => onUntilChange(e.target.value)}
            />
          </div>
        ) : null}
      </div>
      {manualBadge === "BESTSELLER" ? (
        <p className="text-xs text-on-surface-variant">
          Доступен только при ≥10 оплаченных заказах товара (ФЗ-38 ст.5 — достоверность рекламы).
        </p>
      ) : null}
    </section>
  );
}
